import React, { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

const YearlyReport = ({ transactions }) => {
  const yearlyData = useMemo(() => {
    const data = {};
    
    transactions.forEach(transaction => {
      const year = new Date(transaction.date).getFullYear();
      if (!data[year]) {
        data[year] = {
          year,
          deposits: 0,
          withdrawals: 0,
          transactionCount: 0
        };
      }
      
      const amount = Math.abs(transaction.amount);
      if (transaction.type === 'deposit') {
        data[year].deposits += amount;
      } else {
        data[year].withdrawals += amount;
      }
      data[year].transactionCount++;
    });

    return Object.values(data).map(yearData => ({
      ...yearData,
      netProfit: yearData.withdrawals - yearData.deposits,
      roi: ((yearData.withdrawals - yearData.deposits) / yearData.deposits * 100).toFixed(2)
    }));
  }, [transactions]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border rounded shadow">
          <p className="font-medium">Year {label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: ${Math.abs(entry.value).toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="w-full" style={{ backgroundColor: "#FFFFFF" }}>
        <CardHeader>
          <CardTitle>Yearly Performance Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yearlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="deposits" fill="#ef4444" name="Deposits" />
                <Bar dataKey="withdrawals" fill="#16a34a" name="Withdrawals" />
                <Bar dataKey="netProfit" fill="#6366f1" name="Net Profit" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="w-full" style={{ backgroundColor: "#FFFFFF" }}>
        <CardHeader>
          <CardTitle>Yearly ROI Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={yearlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="roi" 
                  stroke="#6366f1" 
                  name="ROI (%)"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default YearlyReport;